import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { PublishService, PublishLinkType } from '../publish/publish.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogService } from '../dialogs/dialog.service';
import { BookmarkModel } from './models/bookmark.model';
import { OrganizationsState } from '../organizations/state/organizations.state';
import { WidgetOptions } from '../publish/models/widget-options.model';
import { PublishState } from '../publish/state/publish.state';
import { AuthState } from '../auth/state/auth/auth.state';
import { DeleteBookmark } from './state/bookmarks.actions';
import { flatMap, take } from 'rxjs/operators';
import {
  PublishDialogComponent,
  PublishDialogData,
} from '../publish/components/publish-dialog/publish-dialog.component';
import { Router } from '@angular/router';
import { BookmarkAction, BookmarkActionEvent } from './models/bookmark-action';
import { isNil } from 'lodash';
import { ContentType } from '../story/models/content-type.enum';
import { BookmarksService } from './bookmarks.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { environment } from 'src/environments/environment';
import { EnvironmentPlatform } from 'src/environments/models/environment-platform';
import { EnvironmentApplication } from 'src/environments/models/environment-application';
import { DialogActions } from '../dialogs/state/dialog.actions';
import { ClipboardService } from '../shared/clipboard.service';
import {
  SaveBookmarkDialogMode,
  SaveBookmarkDialogComponent,
} from '../dialogs/components/save-bookmark-dialog/save-bookmark-dialog.component';
import { DialogIds } from '../dialogs/models/dialog-ids';
import { Observable, of } from 'rxjs';
import { PermissionsUtil } from '../auth/models/permissions/permissions-util';
import { UserRoleModel } from '../auth/models/user-role.model';
import { UserAccessService } from '../shared/components/user-access/user-access.service';
import { BookmarkLimitModel } from './models/bookmark-limit.model';

const loftActions = [BookmarkAction.View, BookmarkAction.Edit];

const excludedNativeActions = [BookmarkAction.Edit, BookmarkAction.Publish];

@Injectable({
  providedIn: 'root',
})
export class BookmarkActionsService {
  constructor(
    private store: Store,
    private publishService: PublishService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private router: Router,
    private bookmarksService: BookmarksService,
    private breakpointObserver: BreakpointObserver,
    private clipboardService: ClipboardService,
    private userAccessService: UserAccessService,
  ) {}

  getBookmarkActions(
    userRole: UserRoleModel,
    bookmark: BookmarkModel,
    isPremiumUser: boolean,
    isTeamUser: boolean,
    options?: {
      excludeActions?: BookmarkAction[];
      includeActions?: BookmarkAction[];
    },
  ): BookmarkAction[] {
    // If no bookmark is provided, return nothing
    if (isNil(bookmark)) {
      return [];
    }

    // get share options
    // When a "bookmark" content type is flagged as 'public' (a Community bookmark) we want to treat
    // it as a module when not owned by the user.
    // This prevents 3rd party users from seeing bookmark management options.
    const tags = bookmark.tags || [];
    const canEditBookmark = PermissionsUtil.canEditBookmark(userRole, bookmark);
    const bookmarkType =
      tags.includes('public') && !canEditBookmark ? ContentType.Module : bookmark.type;

    // get share options
    const shareOptions = this.getBookmarkShareActions(bookmark, bookmarkType);

    let actions: BookmarkAction[] = [];
    switch (bookmarkType) {
      case ContentType.Module:
        actions = [
          BookmarkAction.View,
          BookmarkAction.Edit,
          ...shareOptions,
          BookmarkAction.Divider,
          BookmarkAction.AddToMyLibrary,
        ];

        // Group Modules can be Managed
        if (PermissionsUtil.isGroupModule(bookmark)) {
          actions.push(BookmarkAction.Manage);
        }

        break;

      case ContentType.Bookmark:
        actions = [
          BookmarkAction.View,
          BookmarkAction.Edit,
          ...shareOptions,
          BookmarkAction.Divider,
          BookmarkAction.Manage,
          BookmarkAction.Delete,
        ];
        break;
    }

    actions = actions.filter((bookmarkAction) => {
      // Restrict Loft actions
      if (
        environment.application === EnvironmentApplication.Loft &&
        !loftActions.includes(bookmarkAction)
      ) {
        return false;
      }

      // Filter out any excluded actions
      if (options.excludeActions && options.excludeActions.includes(bookmarkAction)) {
        return false;
      }

      // Filter out only included actions
      if (options.includeActions && !options.includeActions.includes(bookmarkAction)) {
        return false;
      }

      switch (bookmarkAction) {
        case BookmarkAction.View:
          return PermissionsUtil.canViewBookmarkInStudio(userRole, bookmark);

        case BookmarkAction.Edit:
          return PermissionsUtil.canEditInStudio(userRole, bookmark, isPremiumUser);

        case BookmarkAction.Delete:
          return PermissionsUtil.canDeleteBookmark(userRole, bookmark);

        case BookmarkAction.AddToMyLibrary:
          return PermissionsUtil.canAddToMyLibrary(userRole, bookmark, isPremiumUser);

        case BookmarkAction.Manage:
          return PermissionsUtil.canManageBookmark(userRole, bookmark);

        case BookmarkAction.CopyURL:
          return PermissionsUtil.canCopyURL(userRole, bookmark, isPremiumUser, isTeamUser);

        case BookmarkAction.Publish:
          return PermissionsUtil.canPublishBookmark(userRole, bookmark);

        default:
          return true;
      }
    });

    // Certain actions are excluded from the small site due to lack of UI support.
    // This is subject to change as we continue to grow the mobile experience.
    const isSmall = this.breakpointObserver.isMatched('(max-width: 959px)');
    const excludedActions = [BookmarkAction.Edit, BookmarkAction.Publish, BookmarkAction.Manage];

    if (isSmall || environment.platform !== EnvironmentPlatform.Web) {
      actions = actions.filter((action) => {
        return !excludedActions.includes(action) && action !== BookmarkAction.Divider;
      });
    }

    // native (mobile) applications: disable actions
    if (environment.native) {
      actions = actions.filter((action) => {
        return !excludedNativeActions.includes(action);
      });
    }

    // Clean up stray dividers
    while (actions[0] === BookmarkAction.Divider) {
      actions.splice(0, 1);
    }
    while (actions[actions.length - 1] === BookmarkAction.Divider) {
      actions.pop();
    }

    return actions;
  }

  handleBookmarkAction(action: BookmarkActionEvent) {
    const bookmark = action.bookmark as BookmarkModel;

    switch (action.name) {
      case BookmarkAction.View:
        this.view(bookmark);
        break;

      case BookmarkAction.Edit:
        this.router.navigate(['/edit'], { queryParams: { id: bookmark.id } });
        break;

      case BookmarkAction.Manage:
        this.manage(bookmark);
        break;

      case BookmarkAction.Delete:
        this.delete(bookmark);
        break;

      case BookmarkAction.CopyURL:
        let _url: string;

        this.generateBookmarkLink(bookmark)
          .pipe(
            flatMap((url) => {
              _url = url;
              return this.clipboardService.copyToClipboard(url, 'url');
            }),
          )
          .subscribe((copied) => {
            if (copied) {
              this.snackbar.open('Successfully copied link to clipboard.');
            } else {
              this.dialogService.openClipboardDialog(_url);
            }
          });
        break;

      case BookmarkAction.AddToMyLibrary:
        this.addToMyLibrary(bookmark);
        break;

      case BookmarkAction.Publish:
        this.publish(bookmark);
        break;
    }
  }

  generateBookmarkLink(bookmark: BookmarkModel) {
    const widgetOptions =
      this.store.selectSnapshot(OrganizationsState.getWidgetOptions) || new WidgetOptions();
    const widgetURL = this.store.selectSnapshot(PublishState.getWidgetURL);
    const lang = this.store.selectSnapshot(AuthState.getUserLanguage);
    const app = this.store.selectSnapshot(AuthState.getSelectedUserApp);
    const productAccountId = this.store.selectSnapshot(
      OrganizationsState.getSelectedOrganizationProductAccountId,
    );

    // get url
    return this.publishService.generateWidgetURL(
      bookmark,
      widgetURL,
      widgetOptions,
      null,
      PublishLinkType.Single,
      lang,
      app,
      productAccountId,
    );
  }

  delete(bookmark: BookmarkModel) {
    const dialogRef = this.dialogService.openConfirmDialog({
      data: {
        titleText: 'Delete 3D model',
        bodyText: 'Are you sure you want to delete this 3D model?',
        confirmText: 'Delete',
        confirmColor: 'warn',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.store.dispatch(new DeleteBookmark(bookmark.id));
        }
      });
  }

  manage(bookmark: BookmarkModel, mode = SaveBookmarkDialogMode.Manage) {
    const options = {
      id: DialogIds.SaveBookmarkDialog,
      data: {
        bookmark: bookmark,
        mode: mode,
      },
    };

    this.dialog.closeAll();
    return this.dialog.open(SaveBookmarkDialogComponent, options);
  }

  view(bookmark: BookmarkModel, extendedParams: any = {}) {
    const { id, title } = bookmark;

    const lang = this.store.selectSnapshot(AuthState.getUserLocale).lang;
    const isCustomWidget = this.publishService.isCustomWidget(bookmark.embed_url_path);

    // Unavailable
    if (bookmark.unavailable) {
      this.snackbar.open(`Error opening "${title}", it is unavailable.`);

      // Custom Widget
    } else if (isCustomWidget) {
      const url = this.publishService.getWidgetURL(
        bookmark,
        bookmark.embed_url,
        new WidgetOptions(),
      );
      const handle = window.open(url);

      handle
        ? this.snackbar.open(`"${title}" loaded in a new tab.`)
        : this.snackbar.open(`Error launching "${title}" in a new tab.`);

      // Normal Bookmark
    } else {
      this.router.navigate(['/view'], { queryParams: { id, lang, ...extendedParams } });
    }
  }

  viewQuiz(bookmark: BookmarkModel) {
    this.view(bookmark, { quiz: true });
  }

  publish(bookmark: BookmarkModel) {
    const isPremiumRestrictedAction = this.isPremiumRestrictedAction(BookmarkAction.Publish);

    if (isPremiumRestrictedAction) {
      this.userAccessService.openPublishPaywall();
    } else {
      this.dialog.open(PublishDialogComponent, {
        width: '90vw',
        height: '95vh',
        data: <PublishDialogData>{
          bookmark: bookmark,
        },
      });
    }
  }

  isPremiumRestrictedAction(action: BookmarkAction): boolean {
    const isPremiumUser = this.store.selectSnapshot(AuthState.isPremiumUser);
    const isTeamUser = this.store.selectSnapshot(OrganizationsState.hasOrganization);
    const isValidTeamUser = isPremiumUser && isTeamUser;

    switch (action) {
      // restricted: Valid Premium Team
      case BookmarkAction.Publish:
        return !isValidTeamUser;
      // default: available
      default:
        return false;
    }
  }

  addToMyLibrary(bookmark: BookmarkModel) {
    this.checkBookmarkLimit().subscribe((model) => {
      if (model.limit_reached) {
        this.userAccessService.openBookmarkSaveLimit();
      } else {
        this.manage(bookmark, SaveBookmarkDialogMode.Copy);
      }
    });
  }

  checkBookmarkLimit(): Observable<BookmarkLimitModel> {
    const isPremium = this.store.selectSnapshot(AuthState.isPremiumUser);
    if (isPremium) {
      return of({ limit_reached: false });
    } else {
      return this.bookmarksService.atMaxFreeBookmarkCount();
    }
  }

  getBookmarkShareActions(bookmark: BookmarkModel, bookmarkType: ContentType): BookmarkAction[] {
    // validate modules
    if (bookmarkType === ContentType.Module) {
      // custom client "modules" are enabled for sharing/publishing
      const isCustomUserModel = !isNil(bookmark.module_accessor_level);
      // collection "modules" are enabled for sharing/publishing
      const isCollectionContent = bookmark.collection_content === true;

      // no actions when not applicable
      if (!isCustomUserModel && !isCollectionContent) {
        return [];
      }
    }

    const userRole = this.store.selectSnapshot(AuthState.getUserRole);
    const isPremiumUser = this.store.selectSnapshot(AuthState.isPremiumUser);
    const isTeamUser = this.store.selectSnapshot(OrganizationsState.hasOrganization);

    const actions: BookmarkAction[] = [];

    // check Copy URL
    const canCopyURL = PermissionsUtil.canCopyURL(userRole, bookmark, isPremiumUser, isTeamUser);
    if (canCopyURL) {
      actions.push(BookmarkAction.CopyURL);
    }

    // check Publish
    const canPublishContent = PermissionsUtil.canPublishBookmark(userRole, bookmark);
    if (canPublishContent) {
      actions.push(BookmarkAction.Publish);
    }

    // add grouping
    return actions.length > 0 ? [BookmarkAction.Divider, ...actions] : [];
  }

  private openLimitDialog() {
    const dialog$ = this.dialogService.openLimitDialog();

    dialog$.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogService.openUpgradeDialog();
      }
    });
  }
}
